/**
 * Une offre de cours ouverte par son lien (`group-attribution-proposals`, `substitution-proposals`) :
 * ce que `<TutorOfferPage>` montre.
 *
 * Deux sources, comme `GroupAttributionProposalScreen` et `SubstitutionProposalScreen` :
 *  - par `hash` (le lien d'un mail) — patron B : la réponse de `…ByHash` est tenue par l'écran
 *    (`raw`), pas lue dans `formData`, où la dernière réponse d'un autre lien pourrait se trouver ;
 *  - par id (la notification) — patron A : l'offre est cherchée dans la liste du compte
 *    (`group_attribution_proposals`, `substitution_proposals`), que l'écran relit au montage.
 */
import { createSelector } from "reselect"

import {
    type ApiGroupAttributionProposal,
    type ApiSubstitutionProposal,
    type ApiTutorOffersUser,
    toUITutorGroupOfferFromLink,
    toUITutorSubstitutionOffer,
} from "../../../adapters/tutor/shared/tutorOfferAdapter"
import { type UITutorOffer } from "../../../ui/types/tutorDispoOffres"
import { selectAccount } from "../../shared/displayedStudent"

/** Le type de l'offre d'une route : un groupe (`group-attribution-proposals`), un remplacement. */
export type TutorOfferLinkKind = "group" | "substitution"

/** Ce que la route dit de l'offre : son type, et son `hash` ou son id. */
export type TutorOfferLink = {
    kind: TutorOfferLinkKind
    hash: string | null
    id: string | number | null
}

const account = (state: unknown) => selectAccount(state as never) as ApiTutorOffersUser | null

const selectOffer = createSelector(
    [
        (state: unknown) => account(state)?.group_attribution_proposals ?? null,
        (state: unknown) => account(state)?.substitution_proposals ?? null,
        (_state: unknown, link: TutorOfferLink) => link.kind,
        (_state: unknown, link: TutorOfferLink) => link.hash,
        (_state: unknown, link: TutorOfferLink) => (link.id == null ? null : String(link.id)),
        (_state: unknown, _link: TutorOfferLink, raw: unknown) => raw,
    ],
    (groups, substitutions, kind, hash, id, raw): UITutorOffer | null => {
        if (hash != null) {
            return kind === "substitution"
                ? toUITutorSubstitutionOffer(raw as ApiSubstitutionProposal | null)
                : toUITutorGroupOfferFromLink(raw as ApiGroupAttributionProposal | null)
        }
        if (id == null) return null
        // `parseInt` comme le legacy : un id de lien arrive en chaîne.
        const wanted = parseInt(id, 10)
        if (kind === "substitution") {
            const rows = Array.isArray(substitutions) ? substitutions : []
            return toUITutorSubstitutionOffer(rows.find((row) => row?.id === wanted))
        }
        const rows = Array.isArray(groups) ? groups : []
        return toUITutorGroupOfferFromLink(rows.find((row) => row?.id === wanted))
    }
)

/** L'offre du lien, ou `null` : introuvable (ou pas encore là — l'écran tient l'attente). */
export function tutorOfferViewModel(state: unknown, link: TutorOfferLink, raw: unknown): UITutorOffer | null {
    return selectOffer(state, link, raw)
}
